import React, { useMemo } from 'react'

import { Button, Grid, MenuItem, Select, Typography } from '@barracuda-internal/bds-core-v2'
import { aliasesSpaceContainerSpacious } from '@barracuda-internal/bds-core-v2/dist/styles/bdstokens/build/ts/all_cudaLight_default'
import { HelpOutline } from '@barracuda-internal/bds-core-v2/dist/Icons/Core'

import { useFormatMessage } from 'lib/localization'

import {
  RECIPIENT_SELECT_NAMES,
  useRecipientPoliciesLogic
} from 'components/pages/inboundSettings/recipientPolicies/useRecipientPoliciesLogic'
import { RecipientPolicyStatus } from 'types/Settings'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import { PageContainerV2 } from 'components/libs/layout/pages/PageContainerV2'
import NoDomainPolicyAccessAlert from 'components/libs/layout/pages/NoDomainPolicyAccessAlert'
import RecipientPoliciesTable from './RecipientPoliciesTable'

const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'
const BASE_I18N_KEY = 'ess.inbound_settings.recipient_policies'

const RecipientPolicies: React.FC = () => {
  const isReadonly = useIsReadonly()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)
  const [state, eventHandlers] = useRecipientPoliciesLogic()

  const mastheadRightContent = useMemo(
    () => (
      <Button
        onClick={() => {
          eventHandlers.onSave()
        }}
        bdsType="interactiveEmphasis"
        data-testid="save-button"
        disabled={state.isDisabledSubmit}
      >
        {formatMessage('save')}
      </Button>
    ),
    [eventHandlers, formatMessage, state.isDisabledSubmit]
  )

  // If form is not dirty (isDisabledSubmit = true) and current policy is "scan", then it was originally "scan" and should be disabled.
  // This is in service of BNESS-30129, eliminating the ability to set a recipient policy to "exempt". This was a helpful
  // capability at one point, so this initial change is being made to eliminate the ability to change away from "scan".
  const canDefaultPolicyBeEdited = !(state.isDisabledSubmit && state.defaultPolicy === RecipientPolicyStatus.scan)

  return useMemo(
    () => (
      <PageContainerV2
        dirtyCheck
        isDirtyForm={state.isDisabledNavigation}
        masthead={{
          pageTitle: formatMessage('title'),
          helperIcon: {
            icon: <HelpOutline />,
            linkContent: {
              href: '/admin/support/help/inbound-recipient-policies',
              rel: 'noopener noreferrer',
              target: '_blank'
            }
          },
          rightContent: mastheadRightContent
        }}
        includeDomainConflict
      >
        {state.hasNoDomainPolicyAccess && <NoDomainPolicyAccessAlert />}
        {!state.hasNoDomainPolicyAccess && (
          <Grid container direction="column" spacing={aliasesSpaceContainerSpacious}>
            <Grid item>
              <Grid container direction="row" alignItems="center">
                <Grid item>
                  <Typography variant="h4">{formatMessage('default_policy_description')}</Typography>
                </Grid>
                <Grid item>
                  <Select
                    bdsContainerType="containerless"
                    name={RECIPIENT_SELECT_NAMES.DEFAULT_POLICY}
                    value={state.defaultPolicy}
                    onChange={eventHandlers.handleOnSelectChange}
                    data-testid="default-policy-field"
                    disabled={isReadonly || !canDefaultPolicyBeEdited}
                  >
                    {Object.keys(RecipientPolicyStatus).map(policyValue => (
                      <MenuItem key={policyValue} value={policyValue}>
                        {formatMessage(policyValue)}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" justifyContent="flex-end">
                <Grid item>
                  <Button
                    onClick={eventHandlers.onBulkEdit}
                    bdsType="interactiveNeutral"
                    data-testid="bulk-button"
                    disabled={isReadonly}
                  >
                    {formatInboundMessage('bulk_edit')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <RecipientPoliciesTable
                data={state.data}
                onAddItem={eventHandlers.onAdd}
                onRemoveItem={eventHandlers.onRemove}
                delegateDirtyFormState={eventHandlers.setIsPoliciesTableDirty}
              />
            </Grid>
          </Grid>
        )}
      </PageContainerV2>
    ),
    [
      state.isDisabledNavigation,
      state.defaultPolicy,
      state.data,
      formatMessage,
      eventHandlers,
      isReadonly,
      formatInboundMessage,
      mastheadRightContent,
      canDefaultPolicyBeEdited,
      state.hasNoDomainPolicyAccess
    ]
  )
}

export default RecipientPolicies
